import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import * as api from '../services/api';

interface SecurityScan {
    id: string;
    siteId: string;
    score: 'A' | 'B' | 'C' | 'D' | 'F';
    status: 'pending' | 'completed' | 'failed';
    timestamp: string;
    issues: {
        severity: 'critical' | 'high' | 'medium' | 'low';
        title: string;
        description: string;
    }[];
}

export default function SecurityPage() {
    const { user } = useAuth();
    const [sites, setSites] = useState<any[]>([]);
    const [selectedSite, setSelectedSite] = useState<string>('');
    const [scanning, setScanning] = useState(false);
    const [lastScan, setLastScan] = useState<SecurityScan | null>(null);

    useEffect(() => {
        loadSites();
    }, []);

    const loadSites = async () => {
        const res = await api.getSites();
        if (res.success && res.data) {
            setSites(res.data);
            if (res.data.length > 0) setSelectedSite(res.data[0].id);
        }
    };

    const handleScan = async () => {
        if (!selectedSite || scanning) return;
        setScanning(true);
        setLastScan(null);

        // Find domain name from ID
        const site = sites.find(s => s.id === selectedSite);
        const domain = site ? site.domain_name : 'wranglr.co.za'; // Fallback

        try {
            // Import dynamically to avoid loading on initial bundle if not needed
            const { runFullAudit } = await import('../services/scanner');
            const results = await runFullAudit(domain);

            // Map to local state format matching ScanResult
            // Note: ScanResult matches existing state interface mostly, but let's be explicit
            setLastScan({
                id: `scan_${Date.now()}`,
                siteId: selectedSite,
                score: results.score,
                status: 'completed',
                timestamp: results.timestamp,
                issues: results.issues
            });
        } catch (e) {
            console.error("Scan Failed", e);
            // Fallback error state
            setLastScan({
                id: 'err',
                siteId: selectedSite,
                score: 'F',
                status: 'failed',
                timestamp: new Date().toISOString(),
                issues: [{ severity: 'high', title: 'Audit Failed', description: 'Could not complete external scan.' }]
            });
        } finally {
            setScanning(false);
        }
    };

    return (
        <div className="tab-content security-page">
            <div className="tab-header">
                <h2>🛡️ Security Center</h2>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <select
                        className="form-input"
                        style={{ width: '250px', margin: 0 }}
                        value={selectedSite}
                        onChange={(e) => setSelectedSite(e.target.value)}
                    >
                        {sites.map(s => <option key={s.id} value={s.id}>{s.domain_name}</option>)}
                    </select>
                    <button
                        className="btn btn-primary"
                        onClick={handleScan}
                        disabled={scanning || !selectedSite}
                    >
                        {scanning ? 'Running Audit...' : 'Start Audit'}
                    </button>
                </div>
            </div>

            {scanning && (
                <div className="card overview-card full-width" style={{ textAlign: 'center', padding: '4rem' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }} className="spin-emoji">📡</div>
                    <h3>Scanning...</h3>
                    <style>{`
                        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                        .spin-emoji { display: inline-block; animation: spin 2s linear infinite; }
                    `}</style>
                </div>
            )}

            {!lastScan && !scanning && (
                <div className="empty-state">
                    <span style={{ fontSize: '3rem', display: 'block', marginBottom: '1rem' }}>🕵️</span>
                    <h3>No Audit Results Yet</h3>
                    <p>Select a site and click "Start Audit" to run a security check.</p>
                </div>
            )}

            {lastScan && !scanning && (
                <div className="security-results">
                    {/* Score Card */}
                    <div className="card overview-card full-width">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h3 style={{ margin: 0 }}>Security Grade</h3>
                            </div>
                            <div className={`grade-badge grade-${lastScan.score}`}>
                                {lastScan.score}
                            </div>
                        </div>
                    </div>

                    {/* Report Card */}
                    <div className="card overview-card full-width" style={{ marginTop: '1.5rem' }}>
                        <h3>Audit Report</h3>
                        <div className="table-responsive">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Severity</th>
                                        <th>Issue</th>
                                        <th>Recommendation</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lastScan.issues.map((issue, i) => (
                                        <tr key={i}>
                                            <td>
                                                <span className={`badge badge-${issue.severity === 'critical' ? 'danger' : issue.severity === 'medium' ? 'warning' : 'success'}`}>
                                                    {issue.severity}
                                                </span>
                                            </td>
                                            <td style={{ fontWeight: 600, minWidth: '150px' }}>{issue.title}</td>
                                            <td style={{ minWidth: '200px' }}>{issue.description}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .grade-badge {
                    font-size: 3rem;
                    font-weight: 800;
                    width: 80px;
                    height: 80px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    border: 4px solid currentColor;
                    flex-shrink: 0;
                }
                .grade-A { color: #48bb78; }
                .grade-B { color: #ed8936; }
                .grade-F { color: #f56565; }
                .grade-D { color: #f6ad55; }
                
                .table-responsive {
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                }
                
                /* Mobile tweaks */
                @media (max-width: 768px) {
                    .security-page .tab-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 1rem;
                    }
                    .security-page .tab-header > div {
                        width: 100%;
                        flex-direction: column;
                    }
                    .security-page select {
                        width: 100% !important;
                    }
                    .security-page button {
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );
}
